import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sdkRequest } from '@eventbrite/legacy-infra.http';

import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';

import {
  mergeTrackingBeaconsResponses,
  transformTrackingBeacons,
  type TrackingBeaconsResponse,
  type TrackingBeacons,
} from './transform';

const MAX_BEACON_PAGES = 50;

const getTrackingBeaconsPage = (eventId: string, page?: number): Promise<TrackingBeaconsResponse> => {
  const pageParam = page ? `?page=${page}` : '';
  return sdkRequest(
    `/events/${eventId}/tracking_beacons/${pageParam}`,
    undefined,
    process.env.NEXT_PUBLIC_PROXY_API_V3 ? '/e' : '',
  );
};

/**
 * Fetches every page of the event's tracking beacons. The backend paginates
 * this endpoint (PaginationMixin; default 50 rows/page, server-capped at 200),
 * so a single request silently truncates the list for organizers with many
 * matching beacons — which made saved pixels (e.g. an "All events" GA4 beacon)
 * never fire on the listing page (EB-323621). Loop until `has_more_items` is
 * false and concatenate.
 *
 * Bounding: when the server reports `object_count` (always, on this endpoint),
 * the loop is bounded by the server's OWN count — `ceil(object_count /
 * page_size) + 1` pages — so a well-formed response of ANY size is fetched in
 * full (no fixed data ceiling). `MAX_BEACON_PAGES` is only a circuit breaker
 * for a response that lacks a trustworthy count (or whose count disagrees with
 * its own `has_more_items`): in that abnormal case the loop stops at the cap
 * and the promise REJECTS instead of returning a silently partial result —
 * react-query surfaces the error and no pixels fire (fail-closed, same as a
 * server 404), so a truncated pixel set can never be mistaken for complete.
 * A claimed-more-but-empty page also breaks the loop defensively.
 */
const getTrackingBeacons = async (eventId: string): Promise<TrackingBeaconsResponse> => {
  let accumulated = await getTrackingBeaconsPage(eventId);

  const pagination = accumulated.pagination;
  const hasTrustedCount = Boolean(pagination?.object_count && pagination?.page_size);
  const countBound = hasTrustedCount
    ? Math.ceil((pagination?.object_count as number) / (pagination?.page_size as number)) + 1
    : MAX_BEACON_PAGES;
  const maxPages = countBound;

  for (let page = 2; accumulated.pagination?.has_more_items && page <= maxPages; page++) {
    const nextPage = await getTrackingBeaconsPage(eventId, page);
    if (!nextPage.tracking_beacons?.length) {
      // Defensive: a server that keeps claiming more items but returns no
      // rows would otherwise loop; stop rather than spin.
      break;
    }
    accumulated = mergeTrackingBeaconsResponses(accumulated, nextPage);
  }

  if (accumulated.pagination?.has_more_items) {
    throw new Error(
      `[useTrackingBeacons] Event ${eventId}: tracking beacon list truncated after ` +
        `${maxPages} pages without a trustworthy total; the server reports more rows. ` +
        'Refusing to fire a partial pixel set (EB-323621).',
    );
  }

  return accumulated;
};

export const useTrackingBeacons = (): [TrackingBeacons | null, boolean] => {
  const { id: eventId } = useBasicInfo();
  const { data, isLoading } = useQuery({
    // eventId must be part of the key: the client keeps one QueryClient with a
    // 5-min staleTime across navigation, so a constant key would serve one
    // event's beacons (and consent flags) on another event's page.
    queryKey: ['trackingBeacons', eventId],
    queryFn: () => getTrackingBeacons(eventId),
  });
  // Memoize so consumers get a stable reference: transformTrackingBeacons builds
  // a new object each call, which would otherwise re-fire effects keyed on the
  // returned beacons on every re-render (e.g. duplicate pixel fires / Seeka
  // merges once user data resolves).
  const trackingBeacons = useMemo(() => transformTrackingBeacons(data), [data]);
  return [trackingBeacons, isLoading];
};
